import { useCallback, useMemo, useState } from "react";
import type { AssistantRow } from "../api/types";
import { useSort } from "../hooks/useSort";
import { ChartVersionCell, ClusterLink, FilterCell } from "./assistantCells";

// Column set + presets mirror show_assistant_config.py (COLUMNS / COLUMN_PRESETS).
// cluster + namespace are always shown first, so presets list only the rest.
type ColKey = keyof AssistantRow;

const LABELS: Partial<Record<ColKey, string>> = {
    chartVersion: "ChartVer",
    enabled: "Enabled",
    tag: "Tag",
    runnerTag: "RunnerTag",
    imageRegistry: "ImageRegistry",
    llmPoolEndpoint: "LLM Endpoint",
    modelTemplate: "ModelTemplate",
    llmAssistantModel: "AssistantModel",
    llmCodeModel: "CodeModel",
    vllm: "vllm",
    llmAgentGenEndpoint: "AgentGenEndpoint",
    llmMcpServerEndpoint: "McpServerEndpoint",
    llmChromaPath: "ChromaPath",
    llmChromaCollections: "ChromaCollections",
    llmRandomSeed: "RandomSeed",
    memRequest: "MemReq",
    memLimit: "MemLimit",
    cpuRequest: "CpuReq",
    replicas: "Replicas",
    nodeGroup: "NodeGroup",
    imagePullPolicy: "PullPolicy",
};

const DEFAULT_COLS: ColKey[] = [
    "enabled",
    "tag",
    "runnerTag",
    "imageRegistry",
    "llmPoolEndpoint",
    "modelTemplate",
    "llmAssistantModel",
    "llmCodeModel",
    "vllm",
    "llmAgentGenEndpoint",
    "llmMcpServerEndpoint",
    "llmChromaPath",
    "llmChromaCollections",
    "llmRandomSeed",
    "memRequest",
    "memLimit",
    "cpuRequest",
    "replicas",
    "chartVersion",
];

// Canonical column order — drives both the rendered column order and the Columns menu.
const ALL_COLS = Object.keys(LABELS) as ColKey[];

const PRESETS: Record<string, ColKey[]> = {
    default: DEFAULT_COLS,
    versions: ["chartVersion", "tag", "runnerTag", "imagePullPolicy", "imageRegistry"],
    endpoints: ["llmPoolEndpoint", "llmAgentGenEndpoint", "llmMcpServerEndpoint", "llmChromaPath", "llmChromaCollections"],
    model: ["modelTemplate", "llmAssistantModel", "llmCodeModel", "vllm", "llmRandomSeed"],
    resources: ["memRequest", "memLimit", "cpuRequest", "replicas", "nodeGroup"],
    all: ALL_COLS,
};
const PRESET_ORDER = ["default", "versions", "endpoints", "model", "resources", "all"];

// Per-column filtering covers every displayed column except ChromaCollections and the
// resource block (memory/cpu/replicas/nodeGroup). `enabled` is driven by the global
// "enabled only" toggle instead of a column filter. cluster + namespace are always filterable.
const UNFILTERABLE = new Set<ColKey>(["llmChromaCollections", "memRequest", "memLimit", "cpuRequest", "replicas", "nodeGroup", "enabled"]);

function cellText(row: AssistantRow, key: ColKey): string {
    const v = row[key];
    if (typeof v === "boolean") return v ? "yes" : "no";
    return String(v ?? "");
}

export default function AssistantConfigTable({ rows }: { rows: AssistantRow[] }) {
    const [preset, setPreset] = useState<string>("default");
    const [enabledOnly, setEnabledOnly] = useState(true);
    const [fullWidth, setFullWidth] = useState(false);
    // Per-column substring filters keyed by column ("cluster"/"namespace" included).
    const [colFilters, setColFilters] = useState<Record<string, string>>({});
    // Visible data columns (cluster + namespace are always shown). A preset seeds this;
    // the Columns menu then hides/shows individual ones on top of it.
    const [visible, setVisible] = useState<Set<ColKey>>(() => new Set(DEFAULT_COLS));

    const applyPreset = (p: string) => {
        setPreset(p);
        setVisible(new Set(PRESETS[p] ?? DEFAULT_COLS));
    };
    const toggleCol = (col: ColKey) =>
        setVisible((cur) => {
            const next = new Set(cur);
            next.has(col) ? next.delete(col) : next.add(col);
            return next;
        });

    const cols: ColKey[] = useMemo(() => ALL_COLS.filter((c) => visible.has(c)), [visible]);
    // Only filters for currently-shown columns apply, so what you see is what you filter.
    const displayed = useMemo(() => new Set<string>(["cluster", "namespace", ...cols]), [cols]);
    const setFilter = (key: string, val: string) => setColFilters((f) => ({ ...f, [key]: val }));

    const filtered = useMemo(() => {
        const active = Object.entries(colFilters).filter(([key, val]) => val.trim() !== "" && displayed.has(key));
        return rows.filter((r) => {
            if (enabledOnly && !r.enabled) return false;
            for (const [key, val] of active) {
                if (
                    !cellText(r, key as ColKey)
                        .toLowerCase()
                        .includes(val.trim().toLowerCase())
                )
                    return false;
            }
            return true;
        });
    }, [rows, enabledOnly, colFilters, displayed]);

    const sortVal = useCallback((r: AssistantRow, col: string): string => {
        if (col === "cluster") return r.cluster;
        if (col === "namespace") return r.namespace;
        return cellText(r, col as ColKey);
    }, []);
    const { sorted, toggle, arrow } = useSort(filtered, sortVal, "cluster");

    return (
        <div>
            <div className="assistant-filters">
                <label>
                    Preset{" "}
                    <select value={preset} onChange={(e) => applyPreset(e.target.value)}>
                        {PRESET_ORDER.map((p) => (
                            <option key={p} value={p}>
                                {p}
                            </option>
                        ))}
                    </select>
                </label>
                <details className="col-menu">
                    <summary className="btn btn-small">Columns ({cols.length}) ▾</summary>
                    <div className="col-menu-panel">
                        {ALL_COLS.map((col) => (
                            <label key={col}>
                                <input type="checkbox" checked={visible.has(col)} onChange={() => toggleCol(col)} /> {LABELS[col] ?? col}
                            </label>
                        ))}
                    </div>
                </details>
                <label>
                    <input type="checkbox" checked={enabledOnly} onChange={(e) => setEnabledOnly(e.target.checked)} /> enabled only
                </label>
                <label>
                    <input type="checkbox" checked={fullWidth} onChange={(e) => setFullWidth(e.target.checked)} /> full width
                </label>
                {Object.values(colFilters).some((v) => v.trim() !== "") ? (
                    <button className="btn btn-small" onClick={() => setColFilters({})}>
                        Clear filters
                    </button>
                ) : null}
                <span className="muted">
                    {filtered.length} / {rows.length} namespace(s)
                </span>
            </div>

            {rows.length === 0 ? (
                <p className="muted footnote">No assistant config loaded yet.</p>
            ) : (
                <div className={`table-wrap${fullWidth ? " full-bleed" : ""}`}>
                    <table>
                        <thead>
                            <tr>
                                <th className="sortable" onClick={() => toggle("cluster")}>
                                    Cluster <span className="sort-arrow">{arrow("cluster")}</span>
                                </th>
                                <th className="sortable" onClick={() => toggle("namespace")}>
                                    Namespace <span className="sort-arrow">{arrow("namespace")}</span>
                                </th>
                                {cols.map((col) => (
                                    <th key={col} className="sortable" onClick={() => toggle(col)}>
                                        {LABELS[col] ?? col} <span className="sort-arrow">{arrow(col)}</span>
                                    </th>
                                ))}
                            </tr>
                            <tr className="filter-row">
                                <FilterCell col="cluster" value={colFilters.cluster ?? ""} onChange={setFilter} />
                                <FilterCell col="namespace" value={colFilters.namespace ?? ""} onChange={setFilter} />
                                {cols.map((col) =>
                                    UNFILTERABLE.has(col) ? (
                                        <th key={col} />
                                    ) : (
                                        <FilterCell key={col} col={col} value={colFilters[col] ?? ""} onChange={setFilter} />
                                    ),
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((r) => (
                                <tr key={`${r.cluster}/${r.namespace}`}>
                                    <td>
                                        <ClusterLink cluster={r.cluster} url={r.clusterValuesUrl} />
                                    </td>
                                    <td>
                                        {r.nsValuesUrl ? (
                                            <a
                                                className="source-link"
                                                href={r.nsValuesUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="cluster-app-values.yaml"
                                            >
                                                {r.namespace}
                                            </a>
                                        ) : (
                                            r.namespace
                                        )}
                                    </td>
                                    {cols.map((col) => (
                                        <AssistantCell key={col} row={r} col={col} />
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function AssistantCell({ row, col }: { row: AssistantRow; col: ColKey }) {
    if (col === "enabled" || col === "vllm") {
        const on = row[col] as boolean;
        return <td className={on ? "" : "muted"}>{on ? "✓" : "—"}</td>;
    }
    if (col === "chartVersion") {
        return <ChartVersionCell chartVersion={row.chartVersion} defaultsSource={row.defaultsSource} url={row.chartVersionUrl} />;
    }
    const text = cellText(row, col);
    return <td className={text ? "" : "muted"}>{text || "—"}</td>;
}
